import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ImageViewer from './ImageViewer';
import { products } from '../data/products';
import './Products.css';

const Products = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // Check for category in URL query params
    const params = new URLSearchParams(location.search);
    const category = params.get('category');
    if (category && products.categories.some(cat => cat.id === category)) {
      setActiveCategory(category);
      // Scroll to category section
      const element = document.getElementById(category);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location]);

  const handleWhatsAppInquiry = (product, imageIndex) => {
    const message = encodeURIComponent(
      `Hello! I'm interested in the ${product.name} (Image ${imageIndex + 1}). Can you provide more information about this product?`
    );
    window.open(`https://wa.me/923427374035?text=${message}`, '_blank');
  };

  const filteredCategories = activeCategory === 'all'
    ? products.categories
    : products.categories.filter(category => category.id === activeCategory);

  return (
    <div className="products-page">
      {/* Header Section */}
      <section className="products-header">
        <div className="container">
          <div className="header-content">
            <h1>Our Collections</h1>
            <p>Discover our handcrafted leather products and percussion instruments</p>
          </div>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="categories-filter">
        <div className="container">
          <div className="filter-wrapper">
            <button 
              className={`filter-btn ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => setActiveCategory('all')}
            >
              All Products
            </button>
            {products.categories.map(category => (
              <button
                key={category.id}
                className={`filter-btn ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="products-grid-section">
        <div className="container">
          {filteredCategories.map(category => (
            <div 
              key={category.id} 
              className="category-section"
              id={category.id}
            >
              <div className="category-header">
                <h2>{category.name}</h2>
                <p>{category.description}</p>
                {category.features && (
                  <ul className="category-features">
                    {category.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="products-grid">
                {category.images.map((image, index) => (
                  <div key={index} className="product-item">
                    <div 
                      className="product-image-wrapper"
                      onClick={() => setSelectedImage({ category, index })}
                    >
                      <img 
                        src={`${process.env.PUBLIC_URL}${image}`} 
                        alt={`${category.name} ${index + 1}`}
                        loading="lazy"
                      />
                      <div className="product-overlay">
                        <div className="overlay-content">
                          <div className="view-text">
                            <span className="icon">🔍</span>
                            Click to View Photos
                          </div>
                          <button 
                            className="whatsapp-inquiry-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleWhatsAppInquiry(category, index);
                            }}
                          >
                            <span className="whatsapp-icon">
                              <svg viewBox="0 0 24 24" width="18" height="18">
                                <path fill="currentColor" d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.274.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564c.173.087.289.129.332.202.043.073.043.423-.101.827z"/>
                              </svg>
                            </span>
                            Inquire on WhatsApp
                          </button>
                        </div>
                      </div>
                    </div>
                    {category.details && (
                      <div className="product-details">
                        {Object.entries(category.details).map(([key, value]) => (
                          <div key={key} className="detail-item">
                            <span className="detail-label">{key}:</span>
                            <span className="detail-value">
                              {Array.isArray(value) ? value.join(', ') : value}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Image Viewer */}
      {selectedImage && (
        <ImageViewer
          images={selectedImage.category.images}
          currentIndex={selectedImage.index}
          category={selectedImage.category}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </div>
  );
};

export default Products;
